/**
 * Stage 21 · Customer Technology & Services Marketplace.
 * Enterprise catalogue: browse products, kits, cart with employee & delivery,
 * submit as request/quotation/order per customer commercial policy.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { own } from "@/lib/terminology";
import { toast } from "sonner";
import {
  Search, Sparkles, Package, Layers, ShoppingCart, ClipboardList,
  Plus, Minus, Trash2, X, ChevronRight, ArrowLeft, ShieldAlert, Info,
} from "lucide-react";

const CAT_ICONS = {
  laptop: "💻", desktop: "🖥️", mobile: "📱", tablet: "📲", monitor: "🖥️",
  keyboard: "⌨️", mouse: "🖱️", headset: "🎧", dock: "🔌", bag: "🎒",
  storage: "💾", networking: "📡", software: "💿", other: "📦",
};

export default function Marketplace() {
  const [tab, setTab] = useState("home");
  const [me, setMe] = useState(null);
  const [settings, setSettings] = useState(null);
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [q, setQ] = useState("");
  const [selectedCat, setSelectedCat] = useState("");
  const [priceVisible, setPriceVisible] = useState(true);
  const [cart, setCart] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/auth/me").then(({ data }) => setMe(data?.user ?? data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/marketplace/categories").then(({ data }) => setCats(data.items || []));
    api.get("/marketplace/bundles").then(({ data }) => setBundles(data.items || []));
    api.get("/marketplace/settings").then(({ data }) => setSettings(data)).catch(() => {});
    ensureCart();
  }, []);
  useEffect(() => {
    api.get("/marketplace/products", { params: { q: q || undefined, category_code: selectedCat || undefined, limit: 200 } })
      .then(({ data }) => { setProducts(data.items || []); setPriceVisible(!!data.price_visible); })
      .catch(() => setProducts([]));
  }, [q, selectedCat]);

  const ensureCart = async () => {
    try {
      const { data } = await api.get("/marketplace/carts");
      const draft = (data.items || []).find((c) => c.status === "draft");
      if (draft) return setCart(draft);
      const created = await api.post("/marketplace/carts", { items: [] });
      setCart(created.data);
    } catch (e) { /* ignore for now */ }
  };

  const refreshCart = () => cart ? api.get(`/marketplace/carts/${cart.id}`).then(({ data }) => setCart(data)) : Promise.resolve();

  const addProduct = async (product) => {
    if (!cart) return;
    const items = [...(cart.items || [])];
    const found = items.find((i) => i.product_id === product.id);
    if (found) found.qty = (found.qty || 1) + 1;
    else items.push({ product_id: product.id, qty: 1 });
    try {
      await api.patch(`/marketplace/carts/${cart.id}`, { items });
      toast.success(`Added ${product.name} to cart`);
      refreshCart();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addBundle = async (bundle) => {
    if (!cart) return;
    const items = [...(cart.items || []), { bundle_id: bundle.id, qty: 1 }];
    try {
      await api.patch(`/marketplace/carts/${cart.id}`, { items });
      toast.success(`Kit added: ${bundle.name}`);
      refreshCart();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cartCount = (cart?.items || []).reduce((s, i) => s + (i.qty || 1), 0);

  if (settings && settings.enabled === false) {
    return (
      <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-8 text-center" data-testid="marketplace-disabled">
        <ShieldAlert className="h-6 w-6 text-amber-600 mx-auto" />
        <h1 className="text-xl font-heading font-semibold text-slate-900 mt-3">Marketplace not available</h1>
        <p className="text-sm text-slate-600 mt-2">Please contact your Suntronix customer manager to enable the technology marketplace for your organisation.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6" data-testid="marketplace">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            Marketplace · Stage 21
          </div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">
            Technology & Services Marketplace
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Browse only what your organisation has approved. Every request respects your device policy, budget, inventory and approvals.
          </p>
        </div>
        <button className="h-10 px-4 rounded-md bg-slate-900 text-white text-sm inline-flex items-center gap-2 relative" onClick={() => setTab("cart")} data-testid="mkt-cart-open">
          <ShoppingCart className="h-4 w-4" /> Cart
          {cartCount > 0 && <span className="ml-1 text-[10px] font-semibold bg-amber-400 text-slate-900 rounded-full px-1.5 py-0.5">{cartCount}</span>}
        </button>
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
        {[
          { key: "home",     label: "Home",         icon: Sparkles },
          { key: "browse",   label: "Browse",       icon: Package },
          { key: "kits",     label: "Employee kits",icon: Layers },
          { key: "cart",     label: "Cart",         icon: ShoppingCart },
          { key: "orders",   label: own(me, "orders"), icon: ClipboardList },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} role="tab" aria-selected={tab === key}
            data-testid={`mkt-tab-${key}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors " +
              (tab === key ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>

      {tab === "home" && (
        <HomeSection cats={cats} products={products.slice(0, 6)} bundles={bundles.slice(0, 3)} onCat={(c) => { setSelectedCat(c); setTab("browse"); }} onAddProduct={addProduct} onAddBundle={addBundle} priceVisible={priceVisible} />
      )}
      {tab === "browse" && (
        <BrowseSection cats={cats} products={products} q={q} setQ={setQ} selectedCat={selectedCat} setSelectedCat={setSelectedCat} onAdd={addProduct} priceVisible={priceVisible} />
      )}
      {tab === "kits" && (
        <KitsSection bundles={bundles} products={products} onAdd={addBundle} />
      )}
      {tab === "cart" && (
        <CartSection cart={cart} products={products} bundles={bundles} onChanged={refreshCart} onSubmitted={() => { setTab("orders"); ensureCart(); }} priceVisible={priceVisible} me={me} settings={settings} />
      )}
      {tab === "orders" && (
        <MyOrdersSection />
      )}
    </div>
  );
}

/* ==================================================== Home */
function HomeSection({ cats, products, bundles, onCat, onAddProduct, onAddBundle, priceVisible }) {
  return (
    <div className="space-y-6" data-testid="mkt-home">
      <section>
        <h2 className="text-sm font-medium text-slate-700 mb-3">Quick categories</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {cats.length === 0 ? (
            <div className="col-span-6 text-sm text-slate-500 bg-white border border-slate-200 rounded-md p-6">No categories set up yet. Your Suntronix admin will publish them shortly.</div>
          ) : cats.map((c) => (
            <button key={c.code} onClick={() => onCat(c.code)} className="rounded-md border border-slate-200 bg-white hover:border-slate-900 hover:shadow-sm transition-all p-4 text-left" data-testid={`mkt-cat-${c.code}`}>
              <div className="text-2xl">{CAT_ICONS[c.code] || "📦"}</div>
              <div className="text-sm font-medium text-slate-900 mt-2">{c.name}</div>
            </button>
          ))}
        </div>
      </section>

      {products.length > 0 && (
        <section>
          <div className="flex items-baseline gap-3 mb-3">
            <h2 className="text-sm font-medium text-slate-700">Available for you</h2>
            <span className="text-[10px] text-slate-500">Filtered to your approved catalogue</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {products.map((p) => <ProductCard key={p.id} product={p} onAdd={onAddProduct} priceVisible={priceVisible} />)}
          </div>
        </section>
      )}

      {bundles.length > 0 && (
        <section>
          <h2 className="text-sm font-medium text-slate-700 mb-3">Employee kits</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {bundles.map((b) => <BundleCard key={b.id} bundle={b} onAdd={onAddBundle} />)}
          </div>
        </section>
      )}
    </div>
  );
}

/* ==================================================== Browse */
function BrowseSection({ cats, products, q, setQ, selectedCat, setSelectedCat, onAdd, priceVisible }) {
  return (
    <div className="space-y-4" data-testid="mkt-browse">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 relative min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input className="input pl-9 w-full" placeholder="What do you need?" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mkt-search" />
        </div>
        <select className="input min-w-[180px]" value={selectedCat} onChange={(e) => setSelectedCat(e.target.value)} data-testid="mkt-cat-filter">
          <option value="">All categories</option>
          {cats.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        {(q || selectedCat) && (
          <button onClick={() => { setQ(""); setSelectedCat(""); }} className="text-xs text-slate-500 hover:text-slate-900" data-testid="mkt-browse-clear">Clear</button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {products.length === 0 ? (
          <div className="col-span-3 text-sm text-slate-500 bg-white border border-slate-200 rounded-md p-8 text-center">Nothing matches your filter yet.</div>
        ) : products.map((p) => <ProductCard key={p.id} product={p} onAdd={onAdd} priceVisible={priceVisible} />)}
      </div>
    </div>
  );
}

function ProductCard({ product, onAdd, priceVisible }) {
  const availChip = availabilityChip(product.availability_status);
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-col gap-2 hover:border-slate-900 transition-all" data-testid={`mkt-product-${product.sku || product.id}`}>
      <div className="flex items-start gap-2">
        <div className="text-3xl">{CAT_ICONS[product.category_code] || "📦"}</div>
        <div className="flex-1">
          <div className="text-[10px] text-slate-500 tracking-widest uppercase">{product.brand || "—"}</div>
          <div className="text-sm font-medium text-slate-900 leading-tight">{product.name}</div>
          <div className="text-[10px] font-mono text-slate-500 mt-0.5">{product.sku || product.model || ""}</div>
        </div>
      </div>
      {product.description && <p className="text-xs text-slate-600 line-clamp-3">{product.description}</p>}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={"inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + availChip.cls}>{availChip.label}</span>
        {product.lifecycle_stage && product.lifecycle_stage !== "current" && (
          <span className="inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800">{product.lifecycle_stage.replace(/_/g, " ")}</span>
        )}
      </div>
      <div className="flex items-end justify-between mt-1">
        <div>
          {priceVisible && product.price_inr != null ? (
            <div className="text-lg font-heading font-semibold tabular-nums text-slate-900">₹{Number(product.price_inr).toLocaleString("en-IN")}</div>
          ) : (
            <div className="text-[11px] text-slate-500">Price visible after review</div>
          )}
          <div className="text-[10px] text-slate-500">Warranty {product.warranty_months || 12} mo</div>
        </div>
        <button onClick={() => onAdd(product)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid={`mkt-add-${product.sku || product.id}`}>
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
    </div>
  );
}

function availabilityChip(status) {
  const map = {
    available: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Available" },
    limited: { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Limited stock" },
    procurement_required: { cls: "bg-sky-100 text-sky-800 border-sky-200", label: "Procure" },
    backordered: { cls: "bg-orange-100 text-orange-800 border-orange-200", label: "Backordered" },
    unavailable: { cls: "bg-slate-100 text-slate-500 border-slate-200", label: "Unavailable" },
    country_restricted: { cls: "bg-rose-100 text-rose-800 border-rose-200", label: "Country restricted" },
    approval_required: { cls: "bg-purple-100 text-purple-800 border-purple-200", label: "Approval req." },
  };
  return map[status] || map.available;
}

/* ==================================================== Kits */
function KitsSection({ bundles, products, onAdd }) {
  return (
    <div className="space-y-4" data-testid="mkt-kits">
      <p className="text-sm text-slate-600">Request an entire kit — hardware, services and setup — in one action.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {bundles.length === 0 ? (
          <div className="col-span-2 text-sm text-slate-500 bg-white border border-slate-200 rounded-md p-8 text-center">No kits published yet.</div>
        ) : bundles.map((b) => <BundleCard key={b.id} bundle={b} onAdd={onAdd} products={products} expanded />)}
      </div>
    </div>
  );
}

function BundleCard({ bundle, onAdd, products, expanded }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-900 transition-all" data-testid={`mkt-bundle-${bundle.id}`}>
      <div className="flex items-start gap-3">
        <div className="text-3xl">🧰</div>
        <div className="flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{bundle.kind?.replace(/_/g, " ")}</div>
          <div className="text-sm font-medium text-slate-900">{bundle.name}</div>
          {bundle.description && <p className="text-xs text-slate-600 mt-1">{bundle.description}</p>}
        </div>
      </div>
      {expanded && (
        <div className="mt-3 border-t border-slate-100 pt-2 space-y-1 text-xs text-slate-600">
          <div className="font-medium text-slate-700">Includes:</div>
          {(bundle.items || []).map((it, i) => (
            <div key={i} className="pl-2">• {itemLabel(it, products)} × {it.qty || 1}</div>
          ))}
          {(bundle.services || []).length > 0 && (
            <div className="pt-1"><span className="font-medium text-slate-700">Services:</span> {(bundle.services || []).map((s) => s.label || s.code).join(" · ")}</div>
          )}
        </div>
      )}
      <div className="mt-3 flex justify-end">
        <button onClick={() => onAdd(bundle)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid={`mkt-add-kit-${bundle.id}`}>
          <Plus className="h-4 w-4" /> Add kit
        </button>
      </div>
    </div>
  );
}
function itemLabel(it, products) {
  const p = (products || []).find((x) => x.id === it.product_id);
  return p?.name || it.product_id?.slice(0, 8) || "Product";
}

/* ==================================================== Cart */
function CartSection({ cart, products, bundles, onChanged, onSubmitted, priceVisible, me, settings }) {
  const [employees, setEmployees] = useState([]);
  const [eligibility, setEligibility] = useState(null);

  useEffect(() => {
    api.get("/employees", { params: { limit: 500 } }).then(({ data }) => setEmployees(data.items || [])).catch(() => setEmployees([]));
  }, []);

  useEffect(() => {
    if (cart?.employee_id) {
      api.get("/marketplace/eligibility", { params: { employee_id: cart.employee_id } }).then(({ data }) => setEligibility(data)).catch(() => setEligibility(null));
    } else setEligibility(null);
  }, [cart?.employee_id]);

  const patch = async (upd) => {
    try { await api.patch(`/marketplace/carts/${cart.id}`, upd); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changeQty = (idx, delta) => {
    const items = (cart.items || []).map((x, i) => i === idx ? { ...x, qty: Math.max(1, (x.qty || 1) + delta) } : x);
    patch({ items });
  };
  const removeItem = (idx) => {
    const items = (cart.items || []).filter((_, i) => i !== idx);
    patch({ items });
  };
  const submit = async () => {
    try {
      const { data } = await api.post(`/marketplace/carts/${cart.id}/submit`);
      toast.success(`Submitted as ${data.submit_as}: ${data.ref}`);
      onSubmitted?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!cart) return <div className="text-sm text-slate-500">Setting up your cart…</div>;
  const items = cart.items || [];
  const submitAs = settings?.submit_as || "request";

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="mkt-cart">
      <div className="md:col-span-2 space-y-3">
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="p-3 border-b border-slate-200 flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-medium text-slate-900">Your cart</h2>
            <span className="ml-auto text-xs text-slate-500">{items.length} line{items.length === 1 ? "" : "s"}</span>
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">Your cart is empty. Browse the catalogue and add items or an employee kit.</div>
          ) : (
            <div className="divide-y">
              {items.map((it, i) => {
                const p = products.find((x) => x.id === it.product_id);
                const b = bundles.find((x) => x.id === it.bundle_id);
                const name = p?.name || b?.name || "Item";
                const price = p?.price_inr;
                return (
                  <div key={i} className="p-3 flex items-center gap-3" data-testid={`mkt-cart-line-${i}`}>
                    <div className="text-xl">{it.bundle_id ? "🧰" : (CAT_ICONS[p?.category_code] || "📦")}</div>
                    <div className="flex-1">
                      <div className="text-sm text-slate-900">{name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{p?.sku || (it.bundle_id ? "Kit" : "")}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => changeQty(i, -1)} className="h-7 w-7 border border-slate-200 rounded-md" data-testid={`mkt-cart-dec-${i}`}><Minus className="h-3 w-3 mx-auto" /></button>
                      <span className="text-sm w-6 text-center tabular-nums">{it.qty || 1}</span>
                      <button onClick={() => changeQty(i, +1)} className="h-7 w-7 border border-slate-200 rounded-md" data-testid={`mkt-cart-inc-${i}`}><Plus className="h-3 w-3 mx-auto" /></button>
                    </div>
                    <div className="w-24 text-right text-sm tabular-nums">
                      {priceVisible && price != null ? `₹${(price * (it.qty || 1)).toLocaleString("en-IN")}` : "—"}
                    </div>
                    <button onClick={() => removeItem(i)} className="h-7 w-7 border border-slate-200 rounded-md text-rose-600" data-testid={`mkt-cart-remove-${i}`}><Trash2 className="h-3 w-3 mx-auto" /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <aside className="space-y-3">
        <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2">
          <h3 className="text-sm font-medium text-slate-900">For</h3>
          <select className="input" value={cart.employee_id || ""} onChange={(e) => patch({ employee_id: e.target.value || null })} data-testid="mkt-cart-employee">
            <option value="">Select an employee</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          {eligibility?.requires_approval && (
            <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs p-2" data-testid="mkt-cart-eligibility-warn">
              <Info className="h-3.5 w-3.5 mt-0.5" />
              <div>{eligibility.reason} They currently hold {eligibility.existing_assets?.length || 0} active device(s).</div>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2">
          <h3 className="text-sm font-medium text-slate-900">Delivery</h3>
          <input className="input" placeholder="Delivery address" value={cart.delivery_address || ""} onChange={(e) => patch({ delivery_address: e.target.value })} data-testid="mkt-cart-address" />
          <input className="input" placeholder="Country (e.g. IN)" value={cart.delivery_country || ""} onChange={(e) => patch({ delivery_country: e.target.value })} data-testid="mkt-cart-country" />
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2">
          <h3 className="text-sm font-medium text-slate-900">Governance</h3>
          <input className="input" placeholder="Cost centre" value={cart.cost_centre || ""} onChange={(e) => patch({ cost_centre: e.target.value })} data-testid="mkt-cart-cc" />
          <input className="input" placeholder="Project code" value={cart.project_code || ""} onChange={(e) => patch({ project_code: e.target.value })} data-testid="mkt-cart-project" />
          <textarea className="input min-h-[70px]" placeholder="Notes to Suntronix" value={cart.notes || ""} onChange={(e) => patch({ notes: e.target.value })} data-testid="mkt-cart-notes" />
        </div>

        <button
          onClick={submit}
          disabled={items.length === 0}
          className="w-full h-11 rounded-md bg-slate-900 text-white text-sm font-medium disabled:opacity-40"
          data-testid="mkt-cart-submit">
          Submit as {submitAs.toUpperCase()}
        </button>
        <div className="text-[10px] text-slate-500 text-center">
          {submitAs === "request"  && "Your admin will review and approve as per your policy."}
          {submitAs === "quotation"&& "Suntronix will prepare a formal quotation for your review."}
          {submitAs === "order"    && "This will create a draft order for approval."}
        </div>
      </aside>
    </div>
  );
}

/* ==================================================== My Orders */
function MyOrdersSection() {
  const [data, setData] = useState({ requests: [], orders: [], quotations: [] });
  useEffect(() => { api.get("/marketplace/my-orders").then(({ data }) => setData(data)).catch(() => {}); }, []);
  return (
    <div className="space-y-4" data-testid="mkt-orders">
      <OrderTable title="Requests" rows={data.requests}   numberKey="request_number" statusKey="status"  testId="mkt-orders-requests" />
      <OrderTable title="Quotations" rows={data.quotations} numberKey="quote_number" statusKey="state"  testId="mkt-orders-quotations" />
      <OrderTable title="Orders"    rows={data.orders}    numberKey="order_number" statusKey="state"    testId="mkt-orders-orders" />
    </div>
  );
}
function OrderTable({ title, rows, numberKey, statusKey, testId }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testId}>
      <div className="p-3 border-b border-slate-200 text-sm font-medium text-slate-900">{title} ({rows.length})</div>
      {rows.length === 0 ? (
        <div className="p-4 text-sm text-slate-500">Nothing here yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left py-2 px-3">Number</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3">Items</th>
              <th className="text-right py-2 px-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="py-2 px-3 font-mono text-slate-900">{r[numberKey]}</td>
                <td className="py-2 px-3 text-slate-700">{r[statusKey]}</td>
                <td className="py-2 px-3 text-slate-600">{(r.items || []).length}</td>
                <td className="py-2 px-3 text-right text-[11px] text-slate-500 tabular-nums">{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
